import { Injectable } from '@angular/core';
import { Observable, interval, map, startWith } from 'rxjs';
import { environment } from '../environments/environment';
import { secureRandomIndex } from './utils/random';
import { TipoAcessorio } from './accessory-type.enum';
import { buildSameOriginApiUrl } from './utils/url-helper';

interface GetUpdatesOptions {
  useMock?: boolean;
}

/**
 * Serviço para receber atualizações de veículos em tempo real via Server-Sent Events (SSE).
 * Conecta-se ao backend e escuta eventos de payload contendo dados de veículos e acoplamentos.
 */
@Injectable({ providedIn: 'root' })
export class SocketService {
  private readonly positionApiUrl = environment.positionApiUrl;
  private readonly mockTickMs = 10000;
  private readonly mockSseSeed = {
    vehicle: {
      id: 466541,
      plate: 'JOA0011',
      accessories: [
        { id: 1, name: 'Accessory 1', value: '23.7', type: 3 },
        { id: 2, name: 'Accessory 2', value: '73.8', type: 4 },
      ],
    },
    couplings: [],
  };

  /** Token de autenticação recebido pelo back via evento SSE connected (RNF002) */
  authToken = '';
  
  /**
   * Cria uma conexão SSE e retorna um Observable que emite os dados recebidos.
   * 
   * @param veioid ID do veículo para monitorar
   * @returns Observable que emite objetos Payload com dados de veículos e acoplamentos
   */
  getUpdates(veioid: number, options?: GetUpdatesOptions): Observable<any> {
    if (options?.useMock) {
      const structure = this.buildMockStructure(veioid);
      return interval(this.mockTickMs).pipe(
        startWith(0),
        map(() => this.refreshMockValues(structure))
      );
    }

    return new Observable(observer => {
      const eventSource = new EventSource(`${buildSameOriginApiUrl(this.positionApiUrl)}/sse/${veioid}`);

      // Evento de conexão estabelecida — extrai token enviado pelo back (RNF002)
      eventSource.addEventListener('connected', (event: MessageEvent) => {
        console.log('SSE Conectado:', event.data);
        try {
          const parsed = JSON.parse(event.data);
          if (parsed?.token) {
            this.authToken = parsed.token;
          }
        } catch {
          // data não é JSON; ignora
        }
      });

      // Evento principal com dados de payload
      eventSource.addEventListener('payload', (event: MessageEvent) => {
        try {
          const payload = JSON.parse(event.data);
          observer.next(payload);
        } catch (error) {
          observer.error(error);
        }
      });

      // Tratamento de erros
      eventSource.onerror = (error) => {
        console.error('Erro na conexão SSE:', error);
        
        // Não propagar erro para permitir reconexão automática
        // observer.error(error);
        
        // EventSource reconecta automaticamente
        console.log('SSE irá tentar reconectar automaticamente...');
      };

      // Cleanup quando o Observable for destruído
      return () => {
        eventSource.close();
      };
    });
  }

  private rand(min: number, max: number): number {
    return min + secureRandomIndex(max - min + 1);
  }

  private randBool(): boolean {
    return secureRandomIndex(2) === 0;
  }

  private secureShuffle<T>(items: T[]): T[] {
    const shuffled = [...items];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = secureRandomIndex(i + 1);
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }

  private randPlate(): string {
    const letters = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    const digits = '0123456789';
    const L = () => letters[this.rand(0, letters.length - 1)];
    const D = () => digits[this.rand(0, 9)];
    return `${L()}${L()}${L()}${D()}${L()}${D()}${D()}`;
  }

  // Gera a estrutura fixa uma única vez (nomes, placas, lista de acessórios)
  private buildMockStructure(veioid: number): any {
    let nextAccessoryId = 1;
    const getNextAccessoryId = () => nextAccessoryId++;

    const seedVehicleAccessories = Array.isArray(this.mockSseSeed.vehicle?.accessories)
      ? (this.mockSseSeed.vehicle.accessories as any[])
      : [];

    const vehicleAccessories = seedVehicleAccessories.map((a: any, i) => ({
      id: getNextAccessoryId(),
      name: a.name ?? `Accessory ${i + 1}`,
      type: this.coerceNumeric(a.type, TipoAcessorio.TIPO_ACESSORIO_INDEFINIDO),
      value: a.value,
      ...(a.serialNumber === undefined ? {} : { serialNumber: a.serialNumber }),
      group: 1,
      compatible: true,
    }));

    this.ensureAtLeastOneActionAccessory(vehicleAccessories, getNextAccessoryId);

    const trailerPool = [
      { name: 'Trava de Baú', kind: 'atuador' },
      { name: 'Sensor de Temperatura', kind: 'temperatura' },
      { name: 'Sensor de Pressão', kind: 'pressao' },
      { name: 'Sensor de Abertura', kind: 'sensor' },
      { name: 'Sensor de Impacto', kind: 'sensor' },
      { name: 'Sensor de Umidade', kind: 'umidade' },
      { name: 'Iluminação Interna', kind: 'iluminacao' },
    ];

    const numCouplings = this.rand(1, 3);
    const couplings = Array.from({ length: numCouplings }, (_, i) => {
      const numAcc = this.rand(2, 4);
      const shuffled = this.secureShuffle(trailerPool).slice(0, numAcc);
      return {
        plate: this.randPlate(),
        accessories: shuffled.map((a, j) => ({
          id: getNextAccessoryId(),
          name: a.name,
          kind: a.kind,
          type: a.kind === 'atuador' ? TipoAcessorio.TIPO_ACESSORIO_SAIDA_DIGITAL : TipoAcessorio.TIPO_ACESSORIO_ENTRADA_DIGITAL,
          group: 1,
          compatible: true,
        })),
      };
    });

    return { veioid, vehicleAccessories, couplings };
  }

  private ensureAtLeastOneActionAccessory(accessories: any[], getNextAccessoryId: () => number): void {
    const hasActionAccessory = accessories.some(a => this.isActionAccessory(a));
    if (hasActionAccessory) {
      return;
    }

    accessories.push({
      id: getNextAccessoryId(),
      name: 'Trava de Baú',
      type: TipoAcessorio.TIPO_ACESSORIO_SAIDA_DIGITAL,
      group: 1,
      compatible: true,
      value: 'Inativo',
    });
  }

  private isActionAccessory(accessory: any): boolean {
    const name = (accessory?.name ?? '').toString().normalize('NFD').replaceAll(/[\u0300-\u036f]/g, '').toLowerCase();
    const type = this.coerceNumeric(accessory?.type, TipoAcessorio.TIPO_ACESSORIO_INDEFINIDO);
    return name.includes('trava de bau') && type === TipoAcessorio.TIPO_ACESSORIO_SAIDA_DIGITAL;
  }

  private coerceNumeric(value: unknown, fallback: number): number {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  // Atualiza apenas os valores a cada tick, mantendo estrutura estável
  private refreshMockValues(structure: any): any {
    const mapAccessory = (a: any) => {
      if (a.kind === 'temperatura') {
        return {
          ...a,
          type: a.type ?? TipoAcessorio.TIPO_ACESSORIO_TEMPERATURA,
          group: a.group,
          compatible: true,
          status: 'Ativo',
          value: `${this.rand(10, 45)} °C`,
        };
      }
      if (a.kind === 'pressao') {
        return {
          ...a,
          type: a.type ?? TipoAcessorio.TIPO_ACESSORIO_ENTRADA_DIGITAL,
          group: a.group,
          compatible: true,
          status: 'Ativo',
          value: `${this.rand(80, 120)} kPa`,
        };
      }
      if (a.kind === 'umidade') {
        return {
          ...a,
          type: a.type ?? TipoAcessorio.TIPO_ACESSORIO_UMIDADE,
          group: a.group,
          compatible: true,
          status: 'Ativo',
          value: `${this.rand(30, 95)} %`,
        };
      }
      if (a.kind === 'iluminacao') {
        return {
          ...a,
          type: a.type ?? TipoAcessorio.TIPO_ACESSORIO_ENTRADA_DIGITAL,
          group: a.group,
          compatible: true,
          status: 'Ativo',
          value: `${this.rand(20, 100)} lux`,
        };
      }
      if (a.kind === 'atuador') {
        const active = this.randBool();
        const s = active ? 'Ativo' : 'Inativo';
        return {
          ...a,
          type: a.type ?? TipoAcessorio.TIPO_ACESSORIO_SAIDA_DIGITAL,
          group: a.group,
          compatible: true,
          active,
          status: s,
          value: s,
        };
      }
      const s = this.randBool() ? 'Ativo' : 'Inativo';
      const active = s === 'Ativo';
      return {
        ...a,
        type: a.type ?? TipoAcessorio.TIPO_ACESSORIO_ENTRADA_DIGITAL,
        group: a.group,
        compatible: true,
        active,
        status: s,
        value: s,
      };
    };

    return {
      vehicle: {
        veioid: structure.veioid,
        accessories: structure.vehicleAccessories.map(mapAccessory),
      },
      couplings: structure.couplings.map((c: any) => ({
        plate: c.plate,
        status: 'conectado',
        connectionStatus: 'connected',
        accessories: c.accessories.map(mapAccessory),
      })),
    };
  }
}
